import React, { useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Database } from 'lucide-react';

import {
  makeGetReportPortfolioIds,
  makeGetReportProjectIds,
} from '../../../../redux/state-areas/reports/entities/selectors';

const ReportSourceLabel = ({
  reportPortfolioIds,
  reportProjectIds,
  workspacePortfolios,
  workspaceProjects,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const resolve = (ids) =>
    (ids || [])
      .filter(Boolean)
      .map((id) => {
        const portfolio = workspacePortfolios?.find((p) => p._id === id)?.name;
        const project = workspaceProjects?.find((p) => p._id === id)?.title;
        return portfolio || project || null;
      })
      .filter(Boolean);

  const portfolioNames = resolve(reportPortfolioIds?.split(','));
  const projectNames = resolve(reportProjectIds?.split(','));
  const allNames = [...portfolioNames, ...projectNames];

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  }, []);

  if (allNames.length === 0) return null;

  const label =
    allNames.length === 1
      ? allNames[0]
      : `${allNames.length} projects`;

  return (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center gap-1 text-[11px] text-gray-400 cursor-default select-none">
        <Database size={11} className="flex-shrink-0" />
        <span className="truncate max-w-[180px]">{label}</span>
      </div>

      {/* Tooltip — only shown when multiple sources */}
      {isOpen && allNames.length > 1 && (
        <div className="absolute top-full left-0 mt-2 z-[9999] bg-gray-900 text-white rounded-lg shadow-xl border border-gray-700 py-2 min-w-[180px] max-w-[260px]">
          {/* Caret */}
          <div
            className="absolute bottom-full left-4 w-0 h-0"
            style={{
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderBottom: '5px solid #111827',
            }}
          />
          <div className="px-3 pb-1.5 mb-0.5 border-b border-gray-700">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              Data sources
            </span>
          </div>
          <ul className="px-3 pt-1 space-y-1.5 max-h-48 overflow-y-auto">
            {allNames.map((name) => (
              <li key={name} className="flex items-center gap-2 text-xs text-gray-200">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className="truncate">{name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const makeMapStateToProps = () => {
  const getReportPortfolioIds = makeGetReportPortfolioIds();
  const getReportProjectIds = makeGetReportProjectIds();
  return (state, ownProps) => ({
    reportPortfolioIds: getReportPortfolioIds(state, ownProps),
    reportProjectIds: getReportProjectIds(state, ownProps),
    workspaceProjects: state.workspaceReducer.workspaceProjects,
    workspacePortfolios: state.workspaceReducer.workspacePortfolios,
  });
};

export default connect(makeMapStateToProps)(ReportSourceLabel);
